/**
 * Component displays the button with predefined (onstants) values
 *
 */

import React, { useState, useEffect } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Stack,
    MessageBarType,
    MessageBar,
    IIconProps,
    mergeStyleSets,
    Dialog,
    TextField,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    DialogType,
    BaseButton,
    Button,
} from '@fluentui/react';
import { appStyles, logger } from '../../helpers';

type Props = {
    hidden?: boolean;
    onDismiss: (dismissed: boolean) => void;
    checkFolderExist: (name: string) => Promise<boolean> | boolean;
    createFolder: (name: string) => void;
};

const NewFolderDialog = (props: Props) => {
    const uiState = useSelector((state: RootStateOrAny) => state.uiReducer);
    const [name, setName] = useState('');
    const [folderExists, setFolderExists] = useState(false);

    const dialogContentProps = {
        type: DialogType.close,
    };

    useEffect(() => {}, []);

    const classes = mergeStyleSets({
        iconError: {
            fontFamily: 'FabricMDL2Icons-3',
            color: 'white',
        },
        dismissIconError: {
            fontFamily: 'FabricMDL2Icons',
            color: 'white !important',
        },
    });

    const errorIcon: IIconProps = {
        iconName: 'ErrorBadge',
        styles: {
            root: classes.iconError,
        },
    };

    const dismissErrorIcon: IIconProps = {
        iconName: 'Clear',
        styles: {
            root: classes.dismissIconError,
        },
    };

    const saveName = async () => {
        logger.info('The action saveName', 'Dialog');
        if (await props.checkFolderExist(name)) {
            setFolderExists(true);
        } else {
            props.onDismiss(true);
            props.createFolder(name);
            setName('');
        }
    };

    const changeName = (name: string) => {
        setFolderExists(false);
        setName(name);
    };

    const hideDialog = async (
        e?: React.MouseEvent<HTMLElement | BaseButton | Button>,
    ) => {
        e?.stopPropagation();
        props.onDismiss(true);
        setName('');
        setFolderExists(false);
    };

    const closeError = async (
        e?: React.MouseEvent<HTMLElement | BaseButton | Button>,
    ) => {
        e?.stopPropagation();
        setFolderExists(false);
    };

    return (
        <Stack>
            <Dialog
                styles={appStyles.DialogStyle}
                hidden={props.hidden}
                onDismiss={hideDialog}
                dialogContentProps={dialogContentProps}
            >
                <Stack tokens={{ childrenGap: 10 }}>
                    {folderExists ? (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            messageBarIconProps={errorIcon}
                            dismissIconProps={dismissErrorIcon}
                            className={appStyles.globalStyles.error}
                            isMultiline={true}
                            onDismiss={closeError}
                            dismissButtonAriaLabel="Close"
                        >
                            {uiState.translate('newfolder_exists')}
                        </MessageBar>
                    ) : (
                        <></>
                    )}
                    <TextField
                        styles={appStyles.textBoxStyle}
                        value={name}
                        label={uiState.translate('button_newfolder')}
                        onChange={(_e, newValue): void =>
                            changeName(newValue ?? '')
                        }
                    />
                    <DialogFooter>
                        <DefaultButton
                            styles={appStyles.primaryButtonReverse}
                            text={uiState.translate('button_cancel')}
                            onClick={hideDialog}
                        />
                        <PrimaryButton
                            styles={appStyles.primaryButton}
                            text={uiState.translate('button_create')}
                            disabled={name.trim() === ''}
                            onClick={saveName}
                        />
                    </DialogFooter>
                </Stack>
            </Dialog>
        </Stack>
    );
};

export default NewFolderDialog;

NewFolderDialog.propTypes = {
    hidden: PropTypes.bool,
    onDismiss: PropTypes.func,
    createFolder: PropTypes.func,
    checkFolderExist: PropTypes.func,
};
